import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarClock, FileText, TriangleAlert, WifiOff } from "lucide-react"
import { motion } from "motion/react"
import { useEffect } from "react"

import { CreateProfileCard } from "@/components/daily-content/create-profile-card"
import { ProfileCard } from "@/components/daily-content/profile-card"
import { ScheduleCalendar } from "@/components/daily-content/schedule-calendar"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EmptyState } from "@/components/shared/empty-state"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Card } from "@/components/ui/card"
import { DashboardSkeleton } from "@/components/ui/page-skeletons"
import {
  createDailyContentUiStore,
  useIsOffline,
  usePageError,
} from "@/lib/stores/daily-content-ui-store"

export const Route = createFileRoute("/_authed/_admin/daily-content")({
  beforeLoad: () => ({
    dailyContentStore: createDailyContentUiStore(),
  }),
  component: DailyContentAdminRoute,
})

function DailyContentAdminRoute() {
  const store = Route.useRouteContext({
    select: (ctx) => ctx.dailyContentStore,
  })

  const pageError = usePageError(store)
  const isOffline = useIsOffline(store)
  // const isCalendarOpen = useIsCalendarOpen(store)

  const profilesQuery = useProfiles()
  const profiles = profilesQuery.data ?? []

  // Sync drafts when profiles load
  // TODO: remove this effect as tanstack query should be the source of truth for profiles, and drafts should be derived from that state instead of being synced
  useEffect(() => {
    store.getState().syncProfileDrafts(profiles)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, store])

  // Offline listener
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const controller = new AbortController()

    const syncOfflineState = () => {
      store.getState().setIsOffline(!window.navigator.onLine)
    }

    syncOfflineState()
    window.addEventListener("online", syncOfflineState, {
      signal: controller.signal,
    })
    window.addEventListener("offline", syncOfflineState, {
      signal: controller.signal,
    })

    return () => {
      controller.abort()
      // window.removeEventListener("online", syncOfflineState)
      // window.removeEventListener("offline", syncOfflineState)
    }
  }, [store])

  const activeCount = profiles.filter((profile) => profile.isActive).length

  if (profilesQuery.isLoading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="grid gap-5 md:gap-6">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          headerClassName="gap-4"
          kicker={
            <SectionKicker icon={<CalendarClock />}>
              Daily Content
            </SectionKicker>
          }
          title="Run recurring QOTD and FOTD schedules"
          titleClassName="max-w-3xl text-4xl leading-none font-semibold md:text-5xl"
          description="Create publishing profiles, tune cadence, and control active delivery without leaving the admin surface"
          descriptionClassName="max-w-3xl text-base leading-7"
          actions={
            <RefreshButton
              isFetching={profilesQuery.isFetching}
              onClick={() => void profilesQuery.refetch()}
            />
          }
          metrics={
            <>
              <MetricTile
                label="Profiles"
                value={String(profiles.length)}
                detail="Total configured publishing profiles."
              />
              <MetricTile
                label="Active"
                value={String(activeCount)}
                detail="Profiles currently delivering content."
              />
              <MetricTile label="Module" value="SEE" detail="Core module" />
            </>
          }
        />
      </Card>

      <CreateProfileCard store={store} />

      {isOffline ? (
        <Alert className="border-warning/40 bg-warning/15 text-warning">
          <WifiOff className="size-4" />
          <AlertDescription>
            You are offline. Profile changes are disabled until connection is
            restored.
          </AlertDescription>
        </Alert>
      ) : null}

      {profilesQuery.isError ? (
        <StatusAlert tone="danger">
          <div className="flex items-center justify-between gap-3">
            <span>Failed to load content profiles.</span>
            <button
              type="button"
              className="shrink-0 text-sm font-medium underline underline-offset-2 hover:no-underline"
              onClick={() => void profilesQuery.refetch()}
            >
              Retry
            </button>
          </div>
        </StatusAlert>
      ) : null}

      <ScheduleCalendar profiles={profiles} store={store} />

      {pageError ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertDescription>{pageError}</AlertDescription>
          </Alert>
        </motion.div>
      ) : null}

      <div className="grid gap-3">
        {!profilesQuery.isError && profiles.length === 0 ? (
          <EmptyState
            icon={<FileText className="size-4" />}
            title="No content profiles"
            description="Create your first publishing profile to schedule daily content delivery."
          />
        ) : null}

        {profiles.map((profile) => (
          <ProfileCard key={profile.id} profile={profile} store={store} />
        ))}
      </div>
    </div>
  )
}

import { useProfiles } from "@/hooks/queries/profile.queries"
